import { jsPDF } from 'jspdf';
import type { ComiteReunionComplete } from './comiteService';
import type { ChecklistItem } from './checklistService';

function safeDateLabel(dateIso: string | undefined): string {
  if (!dateIso) return '—';
  // dateIso est typiquement "YYYY-MM-DD"
  const d = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateIso;
  return d.toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

function safeText(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

export async function exportComiteReunionPdf(options: {
  reunion: ComiteReunionComplete;
  checklistItems?: ChecklistItem[];
  comiteScope?: string;
}): Promise<void> {
  const { reunion, checklistItems = [], comiteScope } = options;

  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = 48;
  const maxWidth = pageWidth - margin * 2;
  const lineHeight = 14;
  let y = 60;

  const ensureSpace = (needed: number) => {
    if (y + needed <= pageHeight - margin) return;
    doc.addPage();
    y = 60;
  };

  const writeLine = (text: string) => {
    const lines = doc.splitTextToSize(text, maxWidth) as string[];
    for (const line of lines) {
      ensureSpace(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    }
  };

  const sectionTitle = (text: string) => {
    ensureSpace(26);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(text, margin, y);
    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
  };

  // En-tête
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Compte rendu — Réunion', margin, y);
  y += 22;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  writeLine(`Comité: ${safeText(comiteScope || '—')}`);
  writeLine(`Date: ${safeDateLabel(reunion.date_reunion)}`);
  writeLine(`Heure début: ${safeText(reunion.heure_debut || '—')}`);
  writeLine(`Durée prévue: ${safeText(reunion.duree_prevue_min ?? '—')} min`);
  writeLine(`Lieu: ${safeText(reunion.lieu || '—')}`);
  writeLine(`Type: ${safeText(reunion.type_rencontre || reunion.nom || '—')}`);
  writeLine(`Statut: ${safeText(reunion.statut || '—')}`);
  writeLine(`Généré le: ${new Date().toLocaleString('fr-CA')}`);

  y += 8;

  // Objectif principal / verbatim
  sectionTitle('Objectif principal');
  writeLine(safeText(reunion.objectif_principal || '—'));

  if (reunion.verbatim) {
    sectionTitle('Verbatim');
    writeLine(safeText(reunion.verbatim));
  }

  // Participants
  sectionTitle('Participants');
  const participants = reunion.participants || [];
  if (!participants.length) {
    writeLine('—');
  } else {
    for (const p of participants) {
      const present = p.present ? 'Présent' : 'Absent';
      const role = p.role ? ` — ${p.role}` : '';
      const email = p.email ? ` (${p.email})` : '';
      writeLine(`- ${safeText(p.nom)}${role}${email} — ${present}`);
      if (p.notes_participant) writeLine(`  Notes: ${safeText(p.notes_participant)}`);
    }
  }

  // Ordre du jour
  sectionTitle('Ordre du jour');
  const agenda = reunion.agenda || [];
  if (!agenda.length) {
    writeLine('—');
  } else {
    for (const item of agenda) {
      const header = `${item.ordre ?? ''} ${safeText(item.titre)} (${safeText(item.duree_estimee_min ?? '—')} min)`;
      writeLine(`- ${header.trim()}`);
      if (item.description) writeLine(`  ${safeText(item.description)}`);
      if (item.responsable) writeLine(`  Responsable: ${safeText(item.responsable)}`);
      if (item.statut) writeLine(`  Statut: ${safeText(item.statut)}`);
      if (item.notes_item) writeLine(`  Notes: ${safeText(item.notes_item)}`);
    }
  }

  // Notes
  sectionTitle('Notes');
  const notes = reunion.notes || [];
  if (!notes.length) {
    writeLine('—');
  } else {
    for (const n of notes) {
      const type = safeText(n.type || 'note');
      const auteur = n.auteur ? ` — ${n.auteur}` : '';
      writeLine(`- [${type}]${auteur} ${safeText(n.contenu)}`.trim());
    }
  }

  // Actions
  sectionTitle('Actions');
  const actions = reunion.actions || [];
  if (!actions.length) {
    writeLine('—');
  } else {
    for (const a of actions) {
      const echeance = a.echeance ? ` — Échéance: ${safeDateLabel(a.echeance)}` : '';
      const statut = a.statut ? ` — Statut: ${a.statut}` : '';
      writeLine(`- ${safeText(a.titre)} — Resp.: ${safeText(a.responsable)}${echeance}${statut}`);
      if (a.description) writeLine(`  ${safeText(a.description)}`);
      if (a.notes_progression) writeLine(`  Notes: ${safeText(a.notes_progression)}`);
    }
  }

  // Résumé
  sectionTitle('Résumé');
  const resumeText = reunion.resume?.resume_manuel || reunion.resume?.resume_ai || '';
  writeLine(resumeText ? safeText(resumeText) : '—');

  // Checklist
  sectionTitle('Checklist');
  if (!checklistItems.length) {
    writeLine('—');
  } else {
    const sorted = [...checklistItems].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
    for (const item of sorted) {
      const statut = item.statut || '—';
      const obligatoire = item.est_obligatoire ? ' (obligatoire)' : '';
      const responsable = item.responsable ? ` — ${item.responsable}` : '';
      writeLine(`- ${safeText(item.titre)}${obligatoire}${responsable} — ${statut}`);
      if (item.description) writeLine(`  ${safeText(item.description)}`);
    }
  }

  const safeScope = (comiteScope || 'comite').toLowerCase().replace(/[^a-z0-9_-]+/gi, '-');
  const safeDate = safeText(reunion.date_reunion || '').trim() || 'date';
  const filename = `${safeScope}-reunion-${safeDate}.pdf`;
  doc.save(filename);
}

